import React, {useEffect, useMemo, useRef, useState} from 'react';
import useOutsideClick from 'hooks/useOutsideClick';
import cn from 'classnames';
import moment from 'moment';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useDispatch, useSelector} from 'react-redux';
import ButtonPrimary from 'components/buttons/ButtonPrimary/styles';
import Loading from 'components/shared/Utils/Loading';
import IconAverageRating from 'assets/feedback/icon-average-rating.svg';
import IconNeedsReply from 'assets/feedback/icon-needs-reply.svg';
import IconContacted from 'assets/feedback/icon-contacted.svg';
import IconChevronDown from 'assets/icon-chevron-down.svg';
import IconChevronUp from 'assets/icon-chevron-up.svg';
import {
  CLEAR_FEEDBACK_REDESIGN_STATE,
  CLEAR_FEEDBACK_REVIEWS,
  GET_FEEDBACK_DISCOUNT,
  GET_FEEDBACK_REVIEWS,
  GET_FEEDBACK_SUMMARY,
  SET_FEEDBACK_CONTACT_STATUS
} from 'store/feedback/action';
import {
  useFeedbackReviews,
  useFeedbackReviewsHasNextPage,
  useFeedbackReviewsLoading,
  useFeedbackReviewsPage,
  useFeedbackSummary
} from 'store/feedback/selectors';
import ReviewCard from 'routes/App/feedback/redesign/ReviewCard';
import Stars from 'routes/App/feedback/redesign/Stars';
import {
  FEEDBACK_ORDER_TYPE,
  FEEDBACK_REVIEW_STATUS
} from 'routes/App/feedback/redesign/mockData';
import {
  ClickableTableRow,
  EmptyReviewsMessage,
  FeedbackTab,
  FeedbackTabs,
  FeedbackToolbar,
  ItemsTable,
  OrderTypeDropdown,
  OrderTypeMenu,
  OrderTypeMenuItem,
  OrderTypeTrigger,
  ReviewFeed,
  SummaryCard,
  SummaryCardBody,
  SummaryCardHeader,
  SummaryCardIcon,
  SummaryCardMeta,
  SummaryCardMetaContent,
  SummaryCardMetaTitle,
  SummaryCardTitle,
  SummaryCardValue,
  SummaryCardsRow,
  TableScrollWrapper
} from 'routes/App/feedback/redesign/styles';

const TABS = [
  {id: 'needs_reply', label: 'Needs Reply'},
  {id: 'replied', label: 'Replied'},
  {id: 'all', label: 'All'}
];

const REPLIED_STATUSES = [
  FEEDBACK_REVIEW_STATUS.EMAILED,
  FEEDBACK_REVIEW_STATUS.CALLED,
  FEEDBACK_REVIEW_STATUS.SKIPPED
];

const STATUS_LABEL = {
  [FEEDBACK_REVIEW_STATUS.NEEDS_REPLY]: 'Needs Reply',
  [FEEDBACK_REVIEW_STATUS.EMAILED]: 'Replied',
  [FEEDBACK_REVIEW_STATUS.CALLED]: 'Replied',
  [FEEDBACK_REVIEW_STATUS.SKIPPED]: 'Replied'
};

const TAB_TO_BFF_STATUS = {
  needs_reply: 'needs_reply',
  replied: 'replied',
  all: null
};

const REVIEWS_FETCH_LIMIT = 20;

const ReviewsTab = () => {
  const dispatch = useDispatch();
  const locationId = useSelector((state) => state.user?.locationId);

  const summary = useFeedbackSummary();
  const reviews = useFeedbackReviews();
  const reviewsLoading = useFeedbackReviewsLoading();
  const reviewsPage = useFeedbackReviewsPage();
  const reviewsHasNextPage = useFeedbackReviewsHasNextPage();

  const [activeTab, setActiveTab] = useState('needs_reply');
  const [orderTypeFilter, setOrderTypeFilter] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [orderTypeMenuOpen, setOrderTypeMenuOpen] = useState(false);
  const [pendingScrollId, setPendingScrollId] = useState(null);
  const orderTypeRef = useRef(null);

  const isInitialLoad = reviewsLoading && reviews.length === 0;

  const fetchNextPage = () => {
    if (reviewsLoading) return;
    dispatch(
      GET_FEEDBACK_REVIEWS({
        page: reviewsPage + 1,
        limit: REVIEWS_FETCH_LIMIT,
        status: TAB_TO_BFF_STATUS[activeTab],
        append: true
      })
    );
  };

  useEffect(() => {
    if (!locationId) return undefined;
    dispatch(GET_FEEDBACK_SUMMARY());
    dispatch(GET_FEEDBACK_DISCOUNT());
    return () => {
      dispatch(CLEAR_FEEDBACK_REDESIGN_STATE());
    };
  }, [locationId]);

  useEffect(() => {
    if (!locationId) return;
    dispatch(CLEAR_FEEDBACK_REVIEWS());
    dispatch(
      GET_FEEDBACK_REVIEWS({
        page: 0,
        limit: REVIEWS_FETCH_LIMIT,
        status: TAB_TO_BFF_STATUS[activeTab]
      })
    );
  }, [locationId, activeTab]);

  useOutsideClick(
    orderTypeRef,
    () => setOrderTypeMenuOpen(false),
    orderTypeMenuOpen
  );

  useEffect(() => {
    if (activeTab !== 'needs_reply') return;
    if (expandedId) return;
    if (!reviews || reviews.length === 0) return;
    setExpandedId(reviews[0].id);
  }, [activeTab, reviews]);

  useEffect(() => {
    if (!pendingScrollId) return;
    if (activeTab !== 'needs_reply' && activeTab !== 'replied') return;
    const el = document.getElementById(`review-${pendingScrollId}`);
    if (el) el.scrollIntoView({behavior: 'smooth', block: 'start'});
    setPendingScrollId(null);
  }, [pendingScrollId, activeTab]);

  const toggleExpand = (id) =>
    setExpandedId((prev) => (prev === id ? null : id));

  const openExpandedFromAll = (review) => {
    if (review.status === FEEDBACK_REVIEW_STATUS.NEEDS_REPLY) {
      setActiveTab('needs_reply');
      setExpandedId(review.id);
    } else {
      setActiveTab('replied');
      setExpandedId(null);
    }
    setPendingScrollId(review.id);
  };

  const selectOrderType = (type) => {
    setOrderTypeFilter(type);
    setOrderTypeMenuOpen(false);
  };

  const orderTypeLabel = orderTypeFilter || 'All';

  const handleContact = (id, status) => {
    dispatch(SET_FEEDBACK_CONTACT_STATUS({reviewId: id, status}));
    dispatch(GET_FEEDBACK_SUMMARY());
  };

  const visibleReviews = useMemo(() => {
    const matchesTab = (review) => {
      if (activeTab === 'needs_reply') {
        return review.status === FEEDBACK_REVIEW_STATUS.NEEDS_REPLY;
      }
      if (activeTab === 'replied') {
        return REPLIED_STATUSES.includes(review.status);
      }
      return true;
    };
    return reviews.filter(
      (review) =>
        matchesTab(review) &&
        (!orderTypeFilter || review.orderType === orderTypeFilter)
    );
  }, [reviews, orderTypeFilter, activeTab]);

  const tabCounts = useMemo(() => {
    const total = summary?.reviewsCount ?? 0;
    const needsReply = summary?.needsReplyCount ?? 0;
    const replied = Math.max(0, total - needsReply);
    return {needs_reply: needsReply, replied, all: total};
  }, [summary]);

  return (
    <>
      <SummaryCardsRow>
        <SummaryCard>
          <SummaryCardHeader>
            <SummaryCardIcon src={IconAverageRating} alt='' />
            <SummaryCardTitle>Average Rating</SummaryCardTitle>
          </SummaryCardHeader>
          <SummaryCardBody>
            <SummaryCardValue>
              {summary?.averageRating != null
                ? Number(summary.averageRating).toFixed(1)
                : '—'}
            </SummaryCardValue>
            <SummaryCardMeta>
              <SummaryCardMetaTitle>
                {summary?.reviewsCount ?? 0} reviews
              </SummaryCardMetaTitle>
              <SummaryCardMetaContent>Last 30 days</SummaryCardMetaContent>
            </SummaryCardMeta>
          </SummaryCardBody>
        </SummaryCard>
        <SummaryCard>
          <SummaryCardHeader>
            <SummaryCardIcon src={IconNeedsReply} alt='' />
            <SummaryCardTitle>Needs Reply</SummaryCardTitle>
          </SummaryCardHeader>
          <SummaryCardBody>
            <SummaryCardValue>{summary?.needsReplyCount ?? 0}</SummaryCardValue>
            <SummaryCardMeta>
              <SummaryCardMetaContent>Customers waiting</SummaryCardMetaContent>
              <SummaryCardMetaContent>to hear from you</SummaryCardMetaContent>
            </SummaryCardMeta>
          </SummaryCardBody>
        </SummaryCard>
        <SummaryCard>
          <SummaryCardHeader>
            <SummaryCardIcon src={IconContacted} alt='' />
            <SummaryCardTitle>Customers Contacted</SummaryCardTitle>
          </SummaryCardHeader>
          <SummaryCardBody>
            <SummaryCardValue>
              {summary?.contactedPercent != null
                ? `${Math.round(summary.contactedPercent)}%`
                : '—'}
            </SummaryCardValue>
            <SummaryCardMeta>
              <SummaryCardMetaTitle>
                {summary?.contactedCount ?? 0} of {summary?.contactedTotal ?? 0}
              </SummaryCardMetaTitle>
              <SummaryCardMetaContent>customers reached</SummaryCardMetaContent>
            </SummaryCardMeta>
          </SummaryCardBody>
        </SummaryCard>
      </SummaryCardsRow>

      <FeedbackToolbar>
        <FeedbackTabs>
          {TABS.map((tab) => (
            <FeedbackTab
              key={tab.id}
              className={cn({isActive: activeTab === tab.id})}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label} ({tabCounts[tab.id]})
            </FeedbackTab>
          ))}
        </FeedbackTabs>
        <OrderTypeDropdown ref={orderTypeRef}>
          <OrderTypeTrigger
            type='button'
            onClick={() => setOrderTypeMenuOpen((prev) => !prev)}
            aria-expanded={orderTypeMenuOpen}
          >
            {orderTypeLabel}
            <img
              src={orderTypeMenuOpen ? IconChevronUp : IconChevronDown}
              alt=''
            />
          </OrderTypeTrigger>
          {orderTypeMenuOpen && (
            <OrderTypeMenu>
              <OrderTypeMenuItem
                type='button'
                className={cn({isActive: !orderTypeFilter})}
                onClick={() => selectOrderType(null)}
              >
                All
              </OrderTypeMenuItem>
              {[FEEDBACK_ORDER_TYPE.DELIVERY, FEEDBACK_ORDER_TYPE.PICKUP].map(
                (type) => (
                  <OrderTypeMenuItem
                    key={type}
                    type='button'
                    className={cn({isActive: orderTypeFilter === type})}
                    onClick={() => selectOrderType(type)}
                  >
                    {type}
                  </OrderTypeMenuItem>
                )
              )}
            </OrderTypeMenu>
          )}
        </OrderTypeDropdown>
      </FeedbackToolbar>

      {isInitialLoad && <Loading />}
      {!isInitialLoad && activeTab === 'all' && (
        <>
          <InfiniteScroll
            dataLength={visibleReviews.length}
            next={fetchNextPage}
            hasMore={reviewsHasNextPage}
            style={{overflow: 'inherit'}}
          >
            {visibleReviews.length === 0 ? (
              <EmptyReviewsMessage>No reviews to show.</EmptyReviewsMessage>
            ) : (
              <TableScrollWrapper>
                <ItemsTable>
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Rating</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Comment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleReviews.map((review) => (
                      <ClickableTableRow
                        key={review.id}
                        onClick={() => openExpandedFromAll(review)}
                      >
                        <td>{review.customerName}</td>
                        <td>
                          <Stars
                            value={review.rating}
                            sentiment={
                              review.rating <= 3 ? 'negative' : 'positive'
                            }
                          />
                        </td>
                        <td>{moment(review.createdAt).format('MMM D')}</td>
                        <td>{STATUS_LABEL[review.status]}</td>
                        <td>{review.comment}</td>
                      </ClickableTableRow>
                    ))}
                  </tbody>
                </ItemsTable>
              </TableScrollWrapper>
            )}
          </InfiniteScroll>
          {reviewsHasNextPage && (
            <ButtonPrimary
              type='button'
              className={`${reviewsLoading ? 'disabled' : ''} loadMore`}
              onClick={fetchNextPage}
            >
              <span>Load More</span>
            </ButtonPrimary>
          )}
        </>
      )}
      {!isInitialLoad && activeTab !== 'all' && (
        <>
          <InfiniteScroll
            dataLength={visibleReviews.length}
            next={fetchNextPage}
            hasMore={reviewsHasNextPage}
            style={{overflow: 'inherit'}}
          >
            <ReviewFeed>
              {visibleReviews.map((review) => (
                <ReviewCard
                  key={review.id}
                  review={review}
                  isExpanded={expandedId === review.id}
                  onToggle={toggleExpand}
                  onContact={handleContact}
                />
              ))}
              {!visibleReviews.length && (
                <EmptyReviewsMessage>No reviews to show.</EmptyReviewsMessage>
              )}
            </ReviewFeed>
          </InfiniteScroll>
          {reviewsHasNextPage && (
            <ButtonPrimary
              type='button'
              className={`${reviewsLoading ? 'disabled' : ''} loadMore`}
              onClick={fetchNextPage}
            >
              <span>Load More</span>
            </ButtonPrimary>
          )}
        </>
      )}
    </>
  );
};

export default ReviewsTab;
